/**
 * Application entry point (T013).
 *
 * Loads and validates the runtime configuration, then renders the app with the bootstrap order:
 *   ThemeProvider > ErrorBoundary > AuthProvider > AuthGate > RouterProvider
 *
 * If configuration is invalid the app renders a ConfigErrorPage instead of the router
 * (fail-fast — Constitution Principle III / research R7).
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { ThemeProvider } from '@fti/design-system';
import '@fti/design-system/styles.css';

import { AuthProvider } from './auth';
import { getAuthConfig } from './config/auth';
import { getConfig } from './config';
import { ConfigValidationError, ConfigError } from './config/errors';
import { ConfigErrorPage } from './components/ConfigErrorPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthGate } from './components/AuthGate';
import { router } from './router';

const rootElement = document.getElementById('root') as HTMLElement;

try {
  // Fail-fast: validate config and derive the single auth configuration before rendering.
  const config = getConfig();
  const authConfig = getAuthConfig();

  console.log(`Starting application in "${config.environment}" environment (auth: ${config.authMode}).`);

  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <ThemeProvider defaultTheme="light">
        <ErrorBoundary>
          <AuthProvider config={authConfig}>
            <AuthGate>
              <RouterProvider router={router} />
            </AuthGate>
          </AuthProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </React.StrictMode>
  );
} catch (error) {
  if (error instanceof ConfigValidationError || error instanceof ConfigError) {
    console.error('Configuration validation failed:', error);
    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <ThemeProvider defaultTheme="light">
          <ConfigErrorPage error={error} />
        </ThemeProvider>
      </React.StrictMode>
    );
  } else {
    console.error('Unexpected error during application initialization:', error);
    throw error;
  }
}
